/**
 * True inside a CabView shell build that can receive files over the WebView bridge.
 * Older shell builds don't set the flag and fall through to the browser path.
 *
 * @return {boolean}
 */
function canNativeDownload() {
  return Boolean(window.ReactNativeWebView && window.IC_NATIVE_DOWNLOAD);
}

/**
 * @param {object} payload
 */
function postNativeDownload(payload) {
  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      url: '/nativeDownload',
      data: JSON.stringify(payload),
    })
  );
}

/**
 * @param {Blob} blob
 * @return {Promise<string>} base64 without the data URL prefix
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => {
      reject(reader.error);
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Give the user a file. Browsers get a normal download; the mobile app shell gets the
 * file over the WebView bridge and opens the share sheet.
 *
 * @param {string|Blob} content
 * @param {string} filename
 * @param {string} mimeType
 * @return {Promise<void>}
 */
export async function downloadFile(content, filename, mimeType) {
  const blob = content instanceof Blob ? content : new Blob([content ?? ''], {type: mimeType});

  if (canNativeDownload()) {
    const base64 = await blobToBase64(blob);
    postNativeDownload({filename, mimeType: mimeType || blob.type, base64});
    return;
  }

  const url = URL.createObjectURL(blob);
  const hiddenElement = document.createElement('a');
  hiddenElement.href = url;
  hiddenElement.download = filename;
  document.body.appendChild(hiddenElement);
  hiddenElement.click();
  document.body.removeChild(hiddenElement);
  // Revoking synchronously can cancel the download in Safari / Firefox
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60 * 1000);
}

/**
 * Give the user a file hosted at a cross-origin URL (e.g. a GCS signed URL).
 *
 * @param {string} url
 * @param {string} filename
 */
export function downloadFileFromUrl(url, filename) {
  if (canNativeDownload()) {
    postNativeDownload({filename, remoteUrl: url});
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
